import { useState } from 'react';

import { TextField, FormControl, FormHelperText } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';

export interface AnswerType extends Record<string, unknown> {
  delay: number;
}

interface AnswerControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: AnswerType;
}

export default function AnswerControls({ updateNodeData, data }: AnswerControlsProps): React.ReactElement {
  const [delay, setDelay] = useState<number>(data.delay ?? 0);
  const translate = useTranslate();

  const onAnswerDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('AnswerControls: onAnswerDataUpdate', { delay });
    if (delay === null) {
      flowchartLogger.debug('AnswerControls: delay is null. Update cancelled.');
      return;
    }
    updateNodeData({ delay });
  };

  return (
    <NodeControlForm onSubmit={onAnswerDataUpdate} data={data} updateNodeData={updateNodeData}>
      <FormControl sx={{ display: 'flex', flexDirection: 'column' }} data-testid='answer-control-form'>
        <TextField
          label={translate('callflows.nodes.answer.delay')}
          type='number'
          slotProps={{ htmlInput: { min: 0 } }}
          value={delay}
          onChange={(event) => {
            let value = parseInt(event.target.value);
            if (isNaN(value)) {
              flowchartLogger.debug('AnswerControls: delay is NaN, setting to 0', { value });
              value = 0;
            }
            setDelay(value);
          }}
        />
        <FormHelperText>{translate('callflows.nodes.answer.delayDescription')}</FormHelperText>
      </FormControl>
    </NodeControlForm>
  );
}
